import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import { downloadReportResponse } from "../components/classeur/download-report-response";
import { Rang1OnlyNotice } from "../components/rang1-only-notice";

type Observation =
  paths["/api/yards/{yardId}/observations"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type NoticeGroup = {
  noticeId: number;
  nAvis: string | null;
  observations: Observation[];
};

type ReportGroup = {
  reportId: number;
  reportNumero: string | null;
  reportDate: string | null;
  notices: NoticeGroup[];
};

type SentResponse = {
  id: number;
  date: string | null;
  reportNumeros: string[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; reports: ReportGroup[]; sent: SentResponse[] };

const reportLabel = (report: ReportGroup) =>
  report.reportNumero ?? `#${report.reportId}`;

/** Default per-avis response text: the non-empty observation drafts joined. */
function defaultResponseText(observations: Observation[]): string {
  return observations
    .map((o) => o.responseDraft?.trim())
    .filter((d): d is string => !isNil(d) && d.length > 0)
    .join("\n");
}

function groupByReport(items: Observation[]): ReportGroup[] {
  const byReport = new Map<number, ReportGroup>();
  for (const obs of items) {
    let report = byReport.get(obs.reportId);
    if (isNil(report)) {
      report = {
        reportId: obs.reportId,
        reportNumero: obs.reportNumero,
        reportDate: obs.reportDate,
        notices: [],
      };
      byReport.set(obs.reportId, report);
    }
    let notice = report.notices.find((n) => n.noticeId === obs.noticeId);
    if (isNil(notice)) {
      notice = { noticeId: obs.noticeId, nAvis: obs.nAvis, observations: [] };
      report.notices.push(notice);
    }
    notice.observations.push(obs);
  }
  return [...byReport.values()];
}

/** The responses already generated, one line each. */
function groupSentResponses(items: Observation[]): SentResponse[] {
  const byResponse = new Map<number, SentResponse>();
  for (const { sentResponse, reportNumero, reportId } of items) {
    if (isNil(sentResponse)) continue;
    const response = byResponse.get(sentResponse.id) ?? {
      ...sentResponse,
      reportNumeros: [],
    };
    const numero = reportNumero ?? `#${reportId}`;
    if (!response.reportNumeros.includes(numero)) {
      response.reportNumeros.push(numero);
    }
    byResponse.set(sentResponse.id, response);
  }
  return [...byResponse.values()];
}

/** Documents the response applies to; all those visés when none was picked. */
function responseDocuments(obs: Observation) {
  const picked = obs.concernedDocuments.filter((doc) =>
    obs.selectedDocumentVersionIds.includes(doc.documentVersionId),
  );
  return picked.length > 0 ? picked : obs.concernedDocuments;
}

/**
 * Screen — "Générer une réponse à rapport d'examen" (N130).
 *
 * Lists the observations flagged "appelle une réponse" whose avis has no
 * response yet, grouped by exam report then by avis. The rang-1 travaux pilot
 * writes the final response text per avis (pre-filled from the observation
 * drafts), reads the form back, then sends it: a persisted `report_response`
 * mailed to the contract that wrote the report, downloadable as CSV.
 */
export function GenererReponseRapport() {
  const { yardId } = useParams<{ yardId: string }>();
  const { hasContractType } = useYardContext();
  const readOnly = !hasContractType("travaux_n_1");
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // Response text keyed by noticeId.
  const [texts, setTexts] = useState<Record<number, string>>({});
  const [reviewed, setReviewed] = useState<ReportGroup | null>(null);
  const [sending, setSending] = useState(false);

  const fetchObservations = useCallback(async () => {
    if (isNil(yardId)) return;
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/observations",
        { params: { path: { yardId } } },
      );
      if (error || isNil(data)) {
        setState({
          status: "error",
          error: "Erreur lors du chargement des observations",
        });
        return;
      }
      const items = data.data.items.filter((o) => o.requiresResponse);
      const reports = groupByReport(items.filter((o) => isNil(o.sentResponse)));
      // Keeps what was typed for the other reports when a response is sent.
      setTexts((prev) => ({
        ...Object.fromEntries(
          reports.flatMap((r) =>
            r.notices.map((n) => [
              n.noticeId,
              defaultResponseText(n.observations),
            ]),
          ),
        ),
        ...prev,
      }));
      setState({
        status: "success",
        reports,
        sent: groupSentResponses(items),
      });
    } catch {
      setState({
        status: "error",
        error: "Erreur lors du chargement des observations",
      });
    }
  }, [yardId]);

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  const answeredNotices = (report: ReportGroup) =>
    report.notices.filter((n) => (texts[n.noticeId] ?? "").trim().length > 0);

  const review = (report: ReportGroup) => {
    if (answeredNotices(report).length === 0) {
      toaster.error({
        title: "Réponse vide",
        description: "Renseignez au moins une réponse avant de générer",
      });
      return;
    }
    setReviewed(report);
  };

  const send = async (report: ReportGroup) => {
    if (isNil(yardId)) return;
    setSending(true);
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/report-responses",
        {
          params: { path: { yardId } },
          body: {
            reportId: report.reportId,
            responses: answeredNotices(report).map((n) => ({
              noticeId: n.noticeId,
              responseText: (texts[n.noticeId] ?? "").trim(),
            })),
          },
        },
      );
      if (error || isNil(data)) {
        toaster.error({
          title: "Réponse non envoyée",
          description:
            error?.error ??
            "Le serveur n'a pas répondu. Réessayez dans un instant.",
        });
        return;
      }
      setReviewed(null);
      toaster.success({
        title: "Réponse générée",
        description:
          data.data.notifiedCount > 0
            ? "La réponse a été enregistrée et envoyée par mail au contrat auteur du rapport"
            : "La réponse a été enregistrée. Aucun mail n'est parti : le contrat auteur du rapport n'a pas d'adresse",
      });
      await fetchObservations();
    } catch {
      toaster.error({
        title: "Réponse non envoyée",
        description: "Le serveur n'a pas pu être joint. Réessayez.",
      });
    } finally {
      setSending(false);
    }
  };

  const download = (responseId: number) => {
    if (isNotNil(yardId)) downloadReportResponse(yardId, responseId);
  };

  const reports = state.status === "success" ? state.reports : [];
  const sent = state.status === "success" ? state.sent : [];
  const total = useMemo(
    () => reports.reduce((n, r) => n + r.notices.length, 0),
    [reports],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Générer une réponse à rapport d'examen
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {total} avis nécessitant une réponse
          </p>
        )}
        {readOnly && (
          <Rang1OnlyNotice>
            Seuls les pilotes de contrat travaux rang 1 génèrent les réponses :
            vous les consultez sans pouvoir en générer.
          </Rang1OnlyNotice>
        )}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4">
          {state.status === "loading" ? (
            <p className="text-sm text-zinc-500">Chargement...</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Aucune observation ne nécessite de réponse
            </p>
          ) : (
            <ul className="space-y-6">
              {reports.map((report) => (
                <li
                  key={report.reportId}
                  className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-800"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                      Rapport {reportLabel(report)}
                    </h3>
                    <Button
                      type="button"
                      onClick={() => review(report)}
                      disabled={readOnly}
                    >
                      Générer la réponse
                    </Button>
                  </div>

                  <ul className="mt-3 space-y-4">
                    {report.notices.map((notice) => (
                      <li key={notice.noticeId}>
                        <p className="text-xs font-medium text-zinc-500">
                          Avis {notice.nAvis ?? `#${notice.noticeId}`}
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {notice.observations.map((obs) => (
                            <li
                              key={obs.id}
                              className="text-xs text-zinc-600 dark:text-zinc-400"
                            >
                              • {obs.description}
                            </li>
                          ))}
                        </ul>
                        <label
                          htmlFor={`reponse-${notice.noticeId}`}
                          className="mt-2 block text-xs font-medium text-zinc-500"
                        >
                          Réponse
                        </label>
                        <textarea
                          id={`reponse-${notice.noticeId}`}
                          rows={3}
                          value={texts[notice.noticeId] ?? ""}
                          disabled={readOnly}
                          onChange={(e) =>
                            setTexts((prev) => ({
                              ...prev,
                              [notice.noticeId]: e.target.value,
                            }))
                          }
                          className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-800"
                        />
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          {sent.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Réponses déjà envoyées
              </h3>
              <p className="mt-1 text-xs text-zinc-500">
                Un avis ne reçoit qu'une réponse : celles-ci ne se régénèrent
                pas.
              </p>
              <ul className="mt-2 space-y-2">
                {sent.map((response) => (
                  <li
                    key={response.id}
                    className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 px-4 py-2 text-sm text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                  >
                    <span>
                      {`Rapport ${response.reportNumeros.join(", ")} : réponse du ${
                        isNil(response.date) ? "-" : formatDay(response.date)
                      }`}
                    </span>
                    <Button
                      type="button"
                      outline
                      onClick={() => download(response.id)}
                    >
                      Télécharger
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {isNotNil(reviewed) && (
        <ResponseReview
          report={reviewed}
          notices={answeredNotices(reviewed)}
          texts={texts}
          sending={sending}
          onSend={() => send(reviewed)}
          onClose={() => setReviewed(null)}
        />
      )}
    </div>
  );
}

type ResponseReviewProps = {
  report: ReportGroup;
  notices: NoticeGroup[];
  texts: Record<number, string>;
  sending: boolean;
  onSend: () => void;
  onClose: () => void;
};

/** The response form, read back before it is sent (N130). */
function ResponseReview({
  report,
  notices,
  texts,
  sending,
  onSend,
  onClose,
}: ResponseReviewProps) {
  const { yard, employee } = useYardContext();
  const author = [employee.firstName, employee.lastName]
    .filter(isNotNil)
    .join(" ");
  const reportDate = isNil(report.reportDate)
    ? ""
    : ` du ${formatDay(report.reportDate)}`;

  return (
    <Modal.Root open onOpenChange={(e) => !e.open && onClose()}>
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="xl">
          <Modal.Header>
            <Modal.Title>Relecture de la réponse</Modal.Title>
          </Modal.Header>
          <div className="px-6 pb-6">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-zinc-500">Date d'envoi</dt>
              <dd>{formatDay(new Date())}</dd>
              <dt className="text-zinc-500">Auteur</dt>
              <dd>{author}</dd>
              <dt className="text-zinc-500">Rapport concerné</dt>
              <dd>{`${reportLabel(report)}${reportDate}`}</dd>
              <dt className="text-zinc-500">Chantier</dt>
              <dd>{yard.name}</dd>
              <dt className="text-zinc-500">N° de formulaire</dt>
              <dd>Attribué à l'envoi</dd>
            </dl>

            <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-700">
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col className="w-16" />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead className="bg-zinc-50 dark:bg-zinc-800">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Avis</th>
                    <th className="px-3 py-2 text-left font-medium">
                      Observation
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      Documents
                    </th>
                    <th className="px-3 py-2 text-left font-medium">Réponse</th>
                  </tr>
                </thead>
                <tbody>
                  {notices.flatMap((notice) =>
                    notice.observations.map((obs, index) => (
                      <tr
                        key={obs.id}
                        className="border-t border-zinc-200 align-top dark:border-zinc-700"
                      >
                        {index === 0 && (
                          <td
                            rowSpan={notice.observations.length}
                            className="px-3 py-2"
                          >
                            {notice.nAvis ?? `#${notice.noticeId}`}
                          </td>
                        )}
                        <td className="px-3 py-2">{obs.description}</td>
                        <td className="px-3 py-2">
                          {responseDocuments(obs)
                            .map((doc) =>
                              doc.index
                                ? `${doc.label} (${doc.index})`
                                : doc.label,
                            )
                            .join(", ")}
                        </td>
                        {index === 0 && (
                          <td
                            rowSpan={notice.observations.length}
                            className="whitespace-pre-line px-3 py-2"
                          >
                            {(texts[notice.noticeId] ?? "").trim()}
                          </td>
                        )}
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-sm text-zinc-500">
              Pièces jointes : aucune
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <Button outline onClick={onClose} disabled={sending}>
                Fermer le brouillon
              </Button>
              <Button onClick={onSend} disabled={sending}>
                {sending ? "Envoi..." : "Envoyer"}
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}
